/**
 * "The user just saved a different voice" — from the Maestro page's Save to whichever live call is
 * listening.
 *
 * The page and the call never share a tree: the call lives in the chat surface's voice bridge and is
 * kept alive (minimized to the island) while the user is on the Personality page, which is the whole
 * point of the in-call Info card's "Customize voice" link. A module-level channel is the narrowest
 * thing both can reach. Import-free on purpose, so a test can drive it without the app's graph.
 *
 * Same feature on the phones: `VoiceCallController.applySavedVoice()` (iOS/Android), called from
 * their Personality Save.
 */

type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe to saved-voice announcements. Returns the unsubscribe. */
export function onVoicePickSaved(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function announceVoicePickSaved(): void {
  for (const listener of [...listeners]) listener();
}

/**
 * Whether a save should reach a live call: only when it CHANGED the pick. Re-saving the voice already
 * stored (the page's Save writes it whenever anything else on the page changed) must not tear down a
 * healthy session. A first-ever pick with nothing stored before it is a change — but the silent init
 * that writes the shown default is not routed here at all.
 */
export function voicePickChanged(
  previous: { provider: string | null; voice: string | null } | null | undefined,
  saved: { provider: string; voice: string },
): boolean {
  return previous?.provider !== saved.provider || previous?.voice !== saved.voice;
}
